import type { ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AppStepperForm } from '@/shared/app-stepper-form';
import { Breadcrumbs } from '@/shared/app-detail-layout/breadcrumbs';
import { styles } from '@/modules/agreements/platform/pages/document-create-page/styles';
import { useDocumentAuthoring } from '@/modules/agreements/platform/pages/document-create-page/hooks/use-document-authoring';
import type { DocumentDraft } from '@/modules/agreements/platform/pages/document-create-page/types';
import { isDraftEmpty } from '@/modules/agreements/platform/pages/document-create-page/types';
import { PartialNotice } from '@/modules/agreements/platform/pages/document-create-page/parts/partial-notice';
import { StepBody } from '@/modules/agreements/platform/pages/document-create-page/parts/step-body';
import type { AuthoringStepId } from '@/modules/agreements/platform/pages/document-create-page/parts/step-body';
import {
  isContentStepValid,
  isDetailsStepValid,
  isRequirementStepValid,
} from '@/modules/agreements/platform/pages/document-create-page/lib/validation';
import type { StepDefinition, StepValidation } from '@/shared/app-stepper-form/types';

/** The breadcrumb trail for the create page. */
const BREADCRUMBS: ReadonlyArray<{ label: string; href?: string }> = [
  { label: 'Platform', href: '/platform/tenants' },
  { label: 'Agreements', href: '/platform/agreements/documents' },
  { label: 'New' },
];

/**
 * The four steps of the journey, as data.
 *
 * Each step's `validate` is the SAME predicate the submit path uses
 * (`firstIncompleteStep`), so a step can never be continuable in the UI while
 * being invalid at submit time. The review step is always valid: it writes
 * nothing, it only reads back.
 */
function buildSteps(
  draft: DocumentDraft,
  onChange: (patch: Partial<DocumentDraft>) => void,
): ReadonlyArray<StepDefinition> {
  const body = (step: AuthoringStepId): ReactNode => (
    <StepBody step={step} draft={draft} onChange={onChange} />
  );
  return [
    {
      id: 'details',
      label: 'Details',
      description:
        'The document’s stable identity. The key is permanent and cannot be changed after creation.',
      required: true,
      content: body('details'),
      validate: (): StepValidation => (isDetailsStepValid(draft) ? 'valid' : 'invalid'),
    },
    {
      id: 'content',
      label: 'Content',
      description:
        'The first version’s text. A version is immutable once published, so this is the content a subject will be bound by.',
      required: true,
      content: body('content'),
      validate: (): StepValidation => (isContentStepValid(draft) ? 'valid' : 'invalid'),
    },
    {
      id: 'requirement',
      label: 'Requirement',
      description:
        'What a subject must do about this document. Without a requirement nothing asks anyone to act.',
      required: true,
      content: body('requirement'),
      validate: (): StepValidation => (isRequirementStepValid(draft) ? 'valid' : 'invalid'),
    },
    {
      id: 'review',
      label: 'Review and publish',
      description:
        'Read back what will be written. Publishing creates the document, its first version and the requirement, in that order.',
      required: true,
      content: body('review'),
      validate: (): StepValidation => 'valid',
    },
  ];
}

/**
 * Author a new agreement document.
 *
 * ONE JOURNEY, THREE WRITES. The API has no "create a complete agreement"
 * endpoint: a document is created, a version is published against it, and a
 * requirement binds a subject to that version. The previous design exposed that
 * as a single form that created the document and then dropped the operator on a
 * detail page to discover the other two steps for themselves — which is why it
 * read as over-engineered: the console was showing its own plumbing instead of
 * the operator's task.
 *
 * The stepper sequences the three writes behind one journey. It does not hide
 * them: the review step names all three, and a failure after the first is
 * reported as a partial outcome with the id of what was created.
 *
 * The page is a thin shell. The draft, the writes and the partial-failure
 * bookkeeping live in `useDocumentAuthoring`; the steps are pure renderings of
 * the draft, built in `lib/steps`.
 */
export function DocumentCreatePage(): ReactNode {
  const navigate = useNavigate();
  const openDocument = (documentId: string): void => {
    void navigate({
      to: '/platform/agreements/documents/$documentId',
      params: { documentId },
    });
  };

  const { draft, update, progress, busy, submit } = useDocumentAuthoring(openDocument);
  const steps = buildSteps(draft, update);

  /**
   * Leaving the flow. The stepper asks for confirmation first when there are
   * unsaved edits, so this only runs once the user has agreed to lose them.
   */
  const leave = (): void => {
    void navigate({ to: '/platform/agreements/documents' });
  };

  /**
   * Whether anything would be lost by leaving.
   *
   * Derived from the draft rather than tracked with a dirty flag: a flag has to
   * be set by every field and cleared by every reset, and the one field that
   * forgets to set it is the one that silently loses the user's work. Comparing
   * against the empty draft cannot drift, because there is nothing to keep in
   * sync.
   */
  const hasUnsavedChanges = !isDraftEmpty(draft);

  return (
    <div className={styles.page}>
      <div className={styles.breadcrumbsRow}>
        <Breadcrumbs breadcrumbs={BREADCRUMBS} />
      </div>

      {progress.documentId ? (
        <PartialNotice progress={progress} onOpenDocument={openDocument} />
      ) : null}

      <AppStepperForm
        title="New agreement document"
        steps={steps}
        submitLabel="Publish document"
        cancelLabel="Cancel"
        isSubmitting={busy}
        hasUnsavedChanges={hasUnsavedChanges}
        onCancel={leave}
        confirmSubmit={{
          title: 'Publish this document?',
          description:
            'Publishing creates the document, its first version and the requirement that asks subjects to act. A published version cannot be edited or withdrawn from this screen.',
          confirmLabel: 'Publish document',
        }}
        onSubmit={() => submit()}
      />
    </div>
  );
}
